import { useEffect, useMemo, useState } from 'react'
import { Navigation } from './components'
import { TaskTree } from './components/tasks/TaskTree'
import { TaskInfoPanel } from './components/tasks/TaskInfoPanel'
import { RunList } from './components/tasks/RunList'
import { RunDetails } from './components/tasks/RunDetails'
import { ALL_TASKS, useTaskRuns, useTasks, type RunSelection } from './components/tasks/hooks'

/**
 * Super Admin → Tasks: the middleware task framework console (docs/tasks/TASK_RUNNERS.md).
 * Left: slugs grouped by their '.' namespace under an 'All' root. Right: the selected
 * task's row fields (top), its runs (bottom-left, newest first) and the selected run's
 * details incl. the live log viewer (bottom-right).
 */
export function Tasks() {
  const tasksQuery = useTasks()
  const tasks = tasksQuery.data?.data ?? []

  const [selectedSlug, setSelectedSlug] = useState<string>(ALL_TASKS)
  const [selectedRun, setSelectedRun] = useState<RunSelection | null>(null)

  const runsQuery = useTaskRuns(selectedSlug)
  const runsData = runsQuery.data?.data
  const runs = useMemo(() => runsData ?? [], [runsData])

  // Default the selection to the most recent run — but never yank a selection the
  // user made themselves (new runs arriving on top must not steal it).
  useEffect(() => {
    if (selectedRun === null && runs.length > 0) {
      setSelectedRun({ slug: runs[0].slug, id: runs[0].id })
    }
  }, [runs, selectedRun])

  const selectSlug = (slug: string) => {
    setSelectedSlug(slug)
    setSelectedRun(null) // the effect re-selects that task's most recent run
  }

  const selectedTask = tasks.find((task) => task.slug === selectedSlug) ?? null

  return (
    <>
      <Navigation />
      <div className="flex" style={{ height: 'calc(100vh - 112px)' }}>
        <aside className="w-64 shrink-0 overflow-y-auto border-r border-gray-200">
          <TaskTree tasks={tasks} selectedSlug={selectedSlug} onSelect={selectSlug} />
        </aside>
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="h-1/5 min-h-[130px] border-b border-gray-200">
            <TaskInfoPanel
              task={selectedTask}
              taskCount={tasks.length}
              onRunScheduled={setSelectedRun}
            />
          </div>
          <div className="flex min-h-0 flex-1">
            <div className="w-1/2 overflow-y-auto border-r border-gray-200">
              <RunList
                runs={runs}
                loading={runsQuery.isLoading}
                showSlug={selectedSlug === ALL_TASKS}
                selectedRunId={selectedRun?.id ?? null}
                onSelect={setSelectedRun}
              />
            </div>
            <div className="w-1/2 min-w-0">
              <RunDetails selection={selectedRun} />
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
